/**
 * The WebAudio graph.
 *
 * ```
 *   recorded take ──► gain ─────────► stereo panner ─┐
 *     or                                            ├─► bus gain ─► master ─► limiter ─► out
 *   oscillator ─► envelope gain ─► stereo panner ───┘
 *   (one chain per voice, torn down when it ends)
 * ```
 *
 * **A recording when there is one, the synthesiser when there is not** (`samples.ts`): the bank
 * loads after the first gesture, and every cue played before it lands — or whose files are
 * missing — sounds exactly as it did before recordings existed.
 *
 * The only file here that touches an `AudioContext`, and so the only one that is
 * not unit tested — the policy it applies lives in `voices.ts`, the numbers in
 * `spatial.ts`, and the sounds in `cues.ts`, all of which are pure. What is left
 * is node plumbing, covered by the manual pass in `doc/platform/DEV-STACK.md`.
 */

import type { Cue } from './cues';
import type { SampleBank } from './samples';
import { attenuation, distanceBetween, stereoPan } from './spatial';
import { VoiceLimiter } from './voices';

/**
 * The mixer's own type: an `AudioSink` plus the node music connects to.
 *
 * Music does **not** go through `play()`. It is a streamed element rather than an
 * oscillator, it must never be voice-stolen by a busy fight, and it wants its own
 * volume — so it gets its own bus, hung off the master before the limiter, and
 * `music.ts` owns it.
 */
export interface Mixer extends AudioSink {
  /** The master bus. Everything audible passes through here and then the limiter. */
  readonly master: AudioNode;
}

/** What the game needs of a mixer. Lets the store wiring be tested with a fake. */
export interface AudioSink {
  /**
   * Plays a cue, optionally from a place in the world. `fallback` plays instead when `cue` is
   * recorded-only and has no take loaded (an elemental hit falling back to the plain impact).
   */
  play(cue: Cue, at: { x: number; z: number } | null, fallback?: Cue): void;
  /** Moves the listener. */
  setListener(at: { x: number; z: number }): void;
  /** Master volume, 0..1. */
  setVolume(volume: number): void;
  /** Silences everything sounding — a zone change, or leaving for the title. */
  stopAll(): void;
  dispose(): void;
}

/** The subset of `AudioContext` the mixer uses, so a test can supply a stub. */
type Ctx = AudioContext;

const BUS_GAINS = { combat: 0.9, environment: 0.7, ui: 1 } as const;

/**
 * How much louder a recording plays than its cue's synthesised `gain`, **chosen by ear against the
 * oscillators**: a take is normalised to −15 dBFS RMS, a full-scale square wave is about −3, so the
 * same `gain` would leave every recording ~12 dB under the beep it replaced. ×3 lands them level
 * and keeps the cues' relative balance — the unique drop still the loudest thing in the game.
 */
export const SAMPLE_GAIN = 3;

/**
 * How long a recording takes to fade when the voice limiter steals it. A beep is gone in 15 ms and
 * so can be; a recording has a tail, and chopping it in 15 ms is the "cut sound" the owner heard in
 * a busy fight. 80 ms is short enough that the voice is free for the next hit, long enough to read
 * as the sound ending.
 */
export const STOLEN_SAMPLE_RELEASE = 0.08;

/**
 * How loud the music bus sits under the effects, before the player's own music
 * slider scales it.
 *
 * Well below the others on purpose: music is a bed, and a track that competes
 * with the sound of being hit is a track the player turns off.
 */
export const MUSIC_BUS_GAIN = 0.45;

/**
 * Builds the graph on an existing context.
 *
 * The context must already be running: browsers refuse to start one without a
 * user gesture, so it is created in the title screen's ENTER handler beside
 * `socket.connect()`. See `main.tsx`.
 */
export function createMixer(context: Ctx, volume: number, samples?: SampleBank): Mixer {
  const limiter = context.createDynamicsCompressor();
  // A soft-knee peak limiter, per §1. Twenty simultaneous oscillators sum well
  // past full scale, and clipping in WebAudio is a hard digital crunch rather
  // than the pleasant kind — this keeps the sum inside the rails.
  limiter.threshold.value = -8;
  limiter.knee.value = 12;
  limiter.ratio.value = 12;
  limiter.attack.value = 0.003;
  limiter.release.value = 0.15;
  limiter.connect(context.destination);

  const master = context.createGain();
  master.gain.value = clamp01(volume);
  master.connect(limiter);

  const buses = {
    combat: context.createGain(),
    environment: context.createGain(),
    ui: context.createGain(),
  };
  for (const [name, node] of Object.entries(buses)) {
    node.gain.value = BUS_GAINS[name as keyof typeof BUS_GAINS];
    node.connect(master);
  }

  const voices = new VoiceLimiter();
  /** Live voices by id — an oscillator or a recorded take — so the limiter can order one stopped. */
  const sounding = new Map<number, { osc: AudioScheduledSourceNode; gain: GainNode; release: number }>();
  let listener = { x: 0, z: 0 };

  const stop = (id: number) => {
    const node = sounding.get(id);
    if (!node) return;
    sounding.delete(id);
    try {
      // Ramp rather than cut: stopping a running oscillator dead produces a
      // click, which is more noticeable than the sound it replaced.
      const t = context.currentTime;
      node.gain.gain.cancelScheduledValues(t);
      node.gain.gain.setValueAtTime(node.gain.gain.value, t);
      node.gain.gain.linearRampToValueAtTime(0, t + node.release);
      node.osc.stop(t + node.release + 0.005);
    } catch {
      // An oscillator that already finished throws on a second stop.
    }
    voices.release(id);
  };

  return {
    master,

    play(requested, at, fallback) {
      // Which cue actually sounds: a recorded-only cue with nothing loaded gives way to its
      // fallback, or to silence — the silence it had before recordings existed.
      const take = requested.sample ? (samples?.pick(requested.sample) ?? null) : null;
      let cue = requested;
      let buffer = take;
      if (!buffer && requested.sampleOnly) {
        if (!fallback) return;
        cue = fallback;
        buffer = fallback.sample ? (samples?.pick(fallback.sample) ?? null) : null;
      }

      const distance = at ? distanceBetween(at, listener) : 0;
      const level = cue.gain * (at ? attenuation(distance) : 1);
      // Silence is not worth a voice, and taking one would let a monster across
      // the map steal a slot from something audible.
      if (level <= 0) return;

      const admission = voices.admit({ category: cue.category, gain: level, distance });
      if (admission.evict !== null) stop(admission.evict);
      if (!admission.admitted) return;

      const t = context.currentTime;
      const panner = context.createStereoPanner();
      panner.pan.value = at ? stereoPan(at.x - listener.x) : 0;

      if (buffer) {
        // The recording: trimmed and faded at both ends already, so no envelope — only a level,
        // and the cue's pitch jitter as a playback rate (§3), which is what varies a take heard
        // a thousand times without a second file.
        const source = context.createBufferSource();
        source.buffer = buffer;
        if (cue.jitter > 0) source.playbackRate.value = 1 + (Math.random() * 2 - 1) * cue.jitter;
        const gain = context.createGain();
        gain.gain.value = Math.min(1, level * SAMPLE_GAIN);
        source.connect(gain).connect(panner).connect(buses[cue.bus]);
        source.start(t);
        sounding.set(admission.id, { osc: source, gain, release: STOLEN_SAMPLE_RELEASE });
        source.onended = () => {
          sounding.delete(admission.id);
          voices.release(admission.id);
        };
        return;
      }

      const osc = context.createOscillator();
      osc.type = cue.wave;

      const detune = cue.jitter > 0 ? 1 + (Math.random() * 2 - 1) * cue.jitter : 1;
      osc.frequency.setValueAtTime(cue.from * detune, t);
      if (cue.to !== cue.from) {
        // Exponential, because pitch is perceived logarithmically — a linear
        // sweep sounds like it slows down as it falls.
        osc.frequency.exponentialRampToValueAtTime(Math.max(1, cue.to * detune), t + cue.duration);
      }

      const envelope = context.createGain();
      // A 6 ms attack rather than an instant one: starting a gain at full value
      // is a step discontinuity, which is audible as a click on every single hit.
      envelope.gain.setValueAtTime(0, t);
      envelope.gain.linearRampToValueAtTime(level, t + 0.006);
      envelope.gain.exponentialRampToValueAtTime(0.0001, t + cue.duration);

      osc.connect(envelope).connect(panner).connect(buses[cue.bus]);
      osc.start(t);
      osc.stop(t + cue.duration + 0.02);

      sounding.set(admission.id, { osc, gain: envelope, release: 0.015 });
      osc.onended = () => {
        sounding.delete(admission.id);
        voices.release(admission.id);
      };
    },

    setListener(at) {
      listener = at;
    },

    setVolume(v) {
      // Ramped, so dragging the slider does not zipper.
      const t = context.currentTime;
      master.gain.cancelScheduledValues(t);
      master.gain.setTargetAtTime(clamp01(v), t, 0.02);
    },

    stopAll() {
      for (const id of [...sounding.keys()]) stop(id);
      voices.clear();
    },

    dispose() {
      for (const id of [...sounding.keys()]) stop(id);
      voices.clear();
      master.disconnect();
      limiter.disconnect();
      for (const bus of Object.values(buses)) bus.disconnect();
    },
  };
}

/**
 * Creates a running `AudioContext`, or null where audio is unavailable.
 *
 * **Must be called from a user gesture.** Every browser refuses to start a
 * context otherwise, and one created too early lands `suspended` and stays that
 * way — the game would be silent with no error anywhere. The title screen's
 * ENTER is the one gesture guaranteed to happen before gameplay.
 */
export function createAudioContext(): AudioContext | null {
  try {
    const Ctor: typeof AudioContext | undefined =
      typeof AudioContext !== 'undefined'
        ? AudioContext
        : (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;

    const ctx = new Ctor();
    // Safari can still hand back a suspended context inside a gesture.
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

function clamp01(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(1, v));
}
